/**
 * Channel Configs Admin View
 *
 * Per-agent channel configuration. Each agent can have its own credentials
 * for Telegram, Discord, Slack, Signal, and WhatsApp. An agent picker +
 * channel type selector at the top controls what is being edited.
 * Settings are saved to the DB and hot-reloaded without gateway restart.
 */

import { html, nothing } from "lit";

// --------------- Types ---------------

export type ChannelConfigEntry = {
  id: string;
  agentId: string;
  channelType: string;
  accountId: string;
  label: string;
  credentials: Record<string, unknown>;
  settings: Record<string, unknown>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export type ChannelTestResult = {
  ok: boolean;
  message: string;
};

export type AgentOption = {
  id: string;
  name?: string;
};

export type ChannelConfigsProps = {
  loading: boolean;
  error: string | null;
  saving: boolean;
  config: ChannelConfigEntry | null;
  form: {
    credentials: Record<string, unknown>;
    settings: Record<string, unknown>;
    label: string;
    isActive: boolean;
  } | null;
  testing: boolean;
  testResult: ChannelTestResult | null;
  agents: AgentOption[];
  selectedAgentId: string | null;
  selectedChannelType: string | null;
  selectedAccountId: string;
  /** All configs for listing in the sidebar */
  allConfigs: ChannelConfigEntry[];
  onSelectAgent: (agentId: string) => void;
  onSelectChannelType: (channelType: string) => void;
  onSelectAccountId: (accountId: string) => void;
  onRefresh: () => void;
  onSave: () => void;
  onDelete: () => void;
  onTest: () => void;
  onFormChange: (section: "credentials" | "settings", field: string, value: unknown) => void;
  onLabelChange: (value: string) => void;
  onActiveChange: (value: boolean) => void;
};

// --------------- Channel Definitions ---------------

type FieldDef = {
  key: string;
  label: string;
  type: "text" | "password" | "number" | "toggle";
  placeholder?: string;
  section: "credentials" | "settings";
  description?: string;
};

const CHANNEL_TYPES = [
  { value: "telegram", label: "Telegram" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "nextcloud", label: "Nextcloud" },
] as const;

const CHANNEL_FIELDS: Record<string, FieldDef[]> = {
  telegram: [
    {
      key: "botToken",
      label: "Bot Token",
      type: "password",
      placeholder: "123456:ABC-DEF...",
      section: "credentials",
    },
    {
      key: "tokenFile",
      label: "Token File Path",
      type: "text",
      placeholder: "/run/secrets/telegram-token",
      section: "credentials",
      description: "Alternative: path to file containing the bot token",
    },
    {
      key: "webhookUrl",
      label: "Webhook URL",
      type: "text",
      placeholder: "https://example.com/webhook/telegram",
      section: "settings",
      description: "Optional: use webhook mode instead of long polling",
    },
    {
      key: "webhookSecret",
      label: "Webhook Secret",
      type: "password",
      placeholder: "random-secret",
      section: "settings",
    },
  ],
  whatsapp: [
    {
      key: "authDir",
      label: "Auth Directory",
      type: "text",
      placeholder: "~/.i-gloobot/oauth/whatsapp/default/",
      section: "credentials",
      description: "Baileys multi-file auth state directory",
    },
    {
      key: "selfChatMode",
      label: "Self Chat Mode",
      type: "toggle",
      section: "settings",
      description: "Bot uses your personal WhatsApp number",
    },
  ],
  nextcloud: [
    {
      key: "url",
      label: "Nextcloud URL",
      type: "text",
      placeholder: "https://cloud.example.com",
      section: "credentials",
      description: "Full URL of the Nextcloud instance",
    },
    {
      key: "username",
      label: "Admin Username",
      type: "text",
      placeholder: "admin",
      section: "credentials",
      description: "Username for OCS/WebDAV API authentication",
    },
    {
      key: "password",
      label: "App Password",
      type: "password",
      placeholder: "xxxxx-xxxxx-xxxxx-xxxxx-xxxxx",
      section: "credentials",
      description: "App password generated in Nextcloud security settings",
    },
  ],
};

// --------------- Helpers ---------------

function fieldInput(
  field: FieldDef,
  value: unknown,
  onChange: (section: "credentials" | "settings", field: string, value: unknown) => void,
) {
  if (field.type === "toggle") {
    return html`
      <div>
        <label style="display:flex;align-items:center;gap:8px;cursor:pointer;">
          <input
            type="checkbox"
            .checked=${Boolean(value)}
            @change=${(e: Event) => onChange(field.section, field.key, (e.target as HTMLInputElement).checked)}
          />
          <span style="font-size:0.85em;color:var(--fg-muted);">${field.label}</span>
        </label>
        ${field.description ? html`<div style="font-size:0.75em;color:var(--fg-muted);margin-top:2px;margin-left:26px;">${field.description}</div>` : nothing}
      </div>
    `;
  }

  return html`
    <div>
      <label style="display:block;font-size:0.85em;color:var(--fg-muted);margin-bottom:4px;">${field.label}</label>
      <input
        type=${field.type === "password" ? "password" : field.type === "number" ? "number" : "text"}
        class="input"
        style="width:100%;box-sizing:border-box;"
        .value=${String(value ?? "")}
        placeholder=${field.placeholder ?? ""}
        @input=${(e: Event) => {
          const raw = (e.target as HTMLInputElement).value;
          const parsed = field.type === "number" ? Number(raw) : raw;
          onChange(field.section, field.key, parsed);
        }}
      />
      ${field.description ? html`<div style="font-size:0.7em;color:var(--fg-muted);margin-top:2px;">${field.description}</div>` : nothing}
    </div>
  `;
}

function testResultBadge(result: ChannelTestResult) {
  const color = result.ok ? "var(--green, #22c55e)" : "var(--red, #ef4444)";
  const bg = result.ok ? "rgba(34, 197, 94, 0.08)" : "rgba(239, 68, 68, 0.08)";
  const icon = result.ok ? "\u2713" : "\u2717";
  return html`
    <div style="display:flex;align-items:center;gap:8px;padding:8px 12px;border-radius:6px;background:${bg};border:1px solid ${color};">
      <span style="color:${color};font-weight:bold;font-size:1.1em;">${icon}</span>
      <span style="font-size:0.9em;">${result.message}</span>
    </div>
  `;
}

// --------------- Render ---------------

export function renderChannelConfigs(props: ChannelConfigsProps) {
  const form = props.form;
  const hasConfig = props.config !== null;
  const channelType = props.selectedChannelType;
  const fields = channelType ? (CHANNEL_FIELDS[channelType] ?? []) : [];
  const credentialFields = fields.filter((f) => f.section === "credentials");
  const settingFields = fields.filter((f) => f.section === "settings");

  return html`
    <div style="display:flex;flex-direction:column;gap:20px;">
      <!-- Selectors: Agent + Channel Type + Account -->
      <div class="card">
        <div class="card-title">Per-Agent Channel Configuration</div>
        <div class="card-sub">Each agent can have its own channel credentials. Select an agent and channel type to configure.</div>
        <div style="margin-top:16px;display:flex;gap:12px;flex-wrap:wrap;align-items:flex-end;">
          <!-- Agent Picker -->
          <div style="flex:1;min-width:180px;max-width:300px;">
            <label style="display:block;font-size:0.85em;color:var(--fg-muted);margin-bottom:4px;">Agent</label>
            <select
              class="input"
              style="width:100%;box-sizing:border-box;padding:8px 12px;"
              @change=${(e: Event) => props.onSelectAgent((e.target as HTMLSelectElement).value)}
            >
              ${
                props.agents.length === 0
                  ? html`
                      <option value="" disabled selected>No agents available</option>
                    `
                  : nothing
              }
              ${props.agents.map(
                (agent) => html`
                  <option value=${agent.id} ?selected=${agent.id === props.selectedAgentId}>
                    ${agent.name ? `${agent.name} (${agent.id})` : agent.id}
                  </option>
                `,
              )}
            </select>
          </div>

          <!-- Channel Type Picker -->
          <div style="flex:1;min-width:160px;max-width:240px;">
            <label style="display:block;font-size:0.85em;color:var(--fg-muted);margin-bottom:4px;">Channel</label>
            <select
              class="input"
              style="width:100%;box-sizing:border-box;padding:8px 12px;"
              @change=${(e: Event) => props.onSelectChannelType((e.target as HTMLSelectElement).value)}
            >
              ${CHANNEL_TYPES.map(
                (ct) => html`
                  <option value=${ct.value} ?selected=${ct.value === props.selectedChannelType}>
                    ${ct.label}
                  </option>
                `,
              )}
            </select>
          </div>

          <!-- Account ID -->
          <div style="flex:1;min-width:140px;max-width:200px;">
            <label style="display:block;font-size:0.85em;color:var(--fg-muted);margin-bottom:4px;">Account ID</label>
            <input
              type="text"
              class="input"
              style="width:100%;box-sizing:border-box;"
              .value=${props.selectedAccountId}
              placeholder="default"
              @input=${(e: Event) => props.onSelectAccountId((e.target as HTMLInputElement).value || "default")}
            />
          </div>

          <!-- Actions -->
          <div style="display:flex;gap:8px;padding-bottom:2px;">
            <button class="btn" @click=${props.onRefresh} ?disabled=${props.loading}>
              ${props.loading ? "Loading..." : "Refresh"}
            </button>
            <button
              class="btn primary"
              @click=${props.onSave}
              ?disabled=${props.saving || props.loading || !form || !props.selectedAgentId || !channelType}
            >
              ${props.saving ? "Saving..." : "Save"}
            </button>
            ${
              hasConfig
                ? html`
                  <button
                    class="btn"
                    style="color:var(--red, #ef4444);border-color:var(--red, #ef4444);"
                    @click=${props.onDelete}
                    ?disabled=${props.saving || props.loading}
                  >
                    Delete
                  </button>
                `
                : nothing
            }
          </div>
        </div>
      </div>

      ${props.error ? html`<div class="callout danger">${props.error}</div>` : nothing}

      ${
        !props.selectedAgentId || !channelType
          ? html`
              <div class="callout info">Select an agent and channel type above to configure credentials.</div>
            `
          : nothing
      }

      ${
        props.selectedAgentId && channelType && props.loading && !form
          ? html`<div class="callout info">Loading ${channelType} configuration for ${props.selectedAgentId}...</div>`
          : nothing
      }

      ${
        props.selectedAgentId && channelType && !props.loading && !hasConfig && !form
          ? html`<div class="callout info">No ${channelType} config for agent "${props.selectedAgentId}" yet. Fill in the credentials and click Save.</div>`
          : nothing
      }

      <!-- Credentials -->
      ${
        props.selectedAgentId && channelType && form && credentialFields.length > 0
          ? html`
            <div class="card">
              <div class="card-title">Credentials</div>
              <div class="card-sub">Authentication tokens and secrets for this ${CHANNEL_TYPES.find((c) => c.value === channelType)?.label ?? channelType} account.</div>
              <div style="margin-top:16px;display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:16px;">
                ${credentialFields.map((f) => fieldInput(f, form.credentials[f.key], props.onFormChange))}
              </div>
            </div>
          `
          : nothing
      }

      <!-- Settings -->
      ${
        props.selectedAgentId && channelType && form && settingFields.length > 0
          ? html`
            <div class="card">
              <div class="card-title">Settings</div>
              <div class="card-sub">Connection and behavioral settings for this account.</div>
              <div style="margin-top:16px;display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:16px;">
                ${settingFields.map((f) => fieldInput(f, form.settings[f.key], props.onFormChange))}
              </div>
            </div>
          `
          : nothing
      }

      <!-- Label + Active Toggle -->
      ${
        props.selectedAgentId && channelType && form
          ? html`
            <div class="card">
              <div class="card-title">Account Metadata</div>
              <div style="margin-top:16px;display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:16px;">
                <div>
                  <label style="display:block;font-size:0.85em;color:var(--fg-muted);margin-bottom:4px;">Label</label>
                  <input
                    type="text"
                    class="input"
                    style="width:100%;box-sizing:border-box;"
                    .value=${form.label}
                    placeholder="My Telegram Bot"
                    @input=${(e: Event) => props.onLabelChange((e.target as HTMLInputElement).value)}
                  />
                  <div style="font-size:0.7em;color:var(--fg-muted);margin-top:2px;">Optional display label for this account</div>
                </div>
                <div>
                  <label style="display:flex;align-items:center;gap:8px;cursor:pointer;">
                    <input
                      type="checkbox"
                      .checked=${form.isActive}
                      @change=${(e: Event) => props.onActiveChange((e.target as HTMLInputElement).checked)}
                    />
                    <span style="font-size:0.85em;color:var(--fg-muted);">Active</span>
                  </label>
                  <div style="font-size:0.7em;color:var(--fg-muted);margin-top:2px;margin-left:26px;">Inactive configs are stored but not used at startup</div>
                </div>
              </div>
            </div>
          `
          : nothing
      }

      <!-- Test Connection -->
      ${
        props.selectedAgentId && channelType && hasConfig
          ? html`
            <div class="card">
              <div class="card-title">Test Connection</div>
              <div class="card-sub">Verify credentials by connecting to the ${CHANNEL_TYPES.find((c) => c.value === channelType)?.label ?? channelType} API.</div>
              <div style="margin-top:16px;">
                <button
                  class="btn primary"
                  @click=${props.onTest}
                  ?disabled=${props.testing || props.loading}
                >
                  ${props.testing ? "Testing..." : "Run Test"}
                </button>
              </div>
              ${
                props.testResult
                  ? html`<div style="margin-top:12px;">${testResultBadge(props.testResult)}</div>`
                  : nothing
              }
            </div>
          `
          : nothing
      }

      <!-- Existing Configs Summary -->
      ${
        props.allConfigs.length > 0
          ? html`
            <div class="card">
              <div class="card-title">All Channel Configs (${props.allConfigs.length})</div>
              <div class="card-sub">All per-agent channel configurations stored in the database.</div>
              <div style="margin-top:16px;overflow-x:auto;">
                <table style="width:100%;border-collapse:collapse;font-size:0.85em;">
                  <thead>
                    <tr style="border-bottom:1px solid var(--border, #333);text-align:left;">
                      <th style="padding:6px 12px;color:var(--fg-muted);">Agent</th>
                      <th style="padding:6px 12px;color:var(--fg-muted);">Channel</th>
                      <th style="padding:6px 12px;color:var(--fg-muted);">Account</th>
                      <th style="padding:6px 12px;color:var(--fg-muted);">Label</th>
                      <th style="padding:6px 12px;color:var(--fg-muted);">Active</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${props.allConfigs.map(
                      (cc) => html`
                        <tr
                          style="border-bottom:1px solid var(--border, #222);cursor:pointer;${cc.agentId === props.selectedAgentId && cc.channelType === props.selectedChannelType && cc.accountId === props.selectedAccountId ? "background:rgba(59,130,246,0.08);" : ""}"
                          @click=${() => {
                            props.onSelectAgent(cc.agentId);
                            props.onSelectChannelType(cc.channelType);
                            props.onSelectAccountId(cc.accountId);
                          }}
                        >
                          <td style="padding:6px 12px;">${cc.agentId}</td>
                          <td style="padding:6px 12px;">${cc.channelType}</td>
                          <td style="padding:6px 12px;">${cc.accountId}</td>
                          <td style="padding:6px 12px;color:var(--fg-muted);">${cc.label || "\u2014"}</td>
                          <td style="padding:6px 12px;">
                            <span style="color:${cc.isActive ? "var(--green, #22c55e)" : "var(--fg-muted, #666)"};">
                              ${cc.isActive ? "Yes" : "No"}
                            </span>
                          </td>
                        </tr>
                      `,
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          `
          : nothing
      }

      <!-- Record Info -->
      ${
        props.config
          ? html`
            <div class="card" style="opacity:0.7;">
              <div class="card-title" style="font-size:0.9em;">Record Info</div>
              <div style="margin-top:8px;display:flex;gap:24px;font-size:0.85em;color:var(--fg-muted);flex-wrap:wrap;">
                <span>Agent: ${props.config.agentId}</span>
                <span>Channel: ${props.config.channelType}</span>
                <span>Account: ${props.config.accountId}</span>
                <span>Created: ${new Date(props.config.createdAt).toLocaleString()}</span>
                <span>Updated: ${new Date(props.config.updatedAt).toLocaleString()}</span>
              </div>
            </div>
          `
          : nothing
      }
    </div>
  `;
}
